import type { LatLng, LeafletMouseEvent } from "leaflet";
import _ from "lodash";
import { type RefObject, useCallback, useEffect } from "react";

import type Light from "@/models/light";
import type { MapHandle, MapMode } from "@/types";

interface DrawSelectionEvent {
    latlng: LatLng;
    originalEvent: MouseEvent & { _simulated?: boolean };
}

/**
 * Props for the `useDrawSelection` hook.
 */
interface DrawSelectionProps {
    /**
     * The ref holding the imperative handle of the `Map` component.
     */
    mapRef: RefObject<MapHandle | null>;
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * The mode-scoped lights the drag selects against.
     */
    modeLights: Light[];
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * Callback to select lights.
     */
    selectLights: (lights: Light | Light[], selectionType?: string) => void;
}

/**
 * Selects every light dragged over while the pointer is held down, for the
 * `select` mode's `Draw` selection type.
 *
 * @param params - The map handle ref, mode state, and selection callback.
 */
export const useDrawSelection = ({
    mapRef,
    mode,
    modeLights,
    pasteModeActivated,
    selectLights,
}: DrawSelectionProps) => {
    const handleDrawSelection = useCallback(
        ({ latlng: startLatLng, originalEvent }: DrawSelectionEvent) => {
            if (pasteModeActivated || !mapRef.current) return;

            const moveEvent = originalEvent._simulated
                ? "touchmove"
                : "mousemove";

            const moveEndEvent = originalEvent._simulated
                ? "touchend"
                : "mouseup";

            const { map } = mapRef.current;
            const availableLights: Light[] = [];
            const selectedLightsIds: string[] = [];

            const selectLight = (light: Light) => {
                selectedLightsIds.push(light.uniqueId);
                selectLights(light, "multiple");
            };

            _.each(modeLights, (light) => {
                if (!light.selected) {
                    availableLights.push(light);
                } else {
                    selectedLightsIds.push(light.uniqueId);
                }
            });

            const starterLight = _.find(availableLights, (light) =>
                light.bounds.contains(startLatLng),
            );

            if (!starterLight) return;

            selectLight(starterLight);

            if (map.dragging.enabled()) {
                map.dragging.disable();
            }

            const move = ({ latlng: moveLatLng }: LeafletMouseEvent) => {
                const selectedLight = _.find(modeLights, (light) =>
                    light.bounds.contains(moveLatLng),
                );

                if (
                    selectedLight &&
                    selectedLightsIds.indexOf(selectedLight.uniqueId) === -1
                ) {
                    selectLight(selectedLight);
                }
            };

            const moveEnd = () => {
                map.off(moveEvent, move);
                map.off(moveEndEvent, moveEnd);

                if (!map.dragging.enabled()) {
                    map.dragging.enable();
                }
            };

            map.on(moveEvent, move);
            map.on(moveEndEvent, moveEnd);
        },
        [mapRef, pasteModeActivated, modeLights, selectLights],
    );

    useEffect(() => {
        if (!mapRef.current) return undefined;

        const { map } = mapRef.current;

        if (mode.name === "select" && mode.props?.type === "Draw") {
            map.on("mousedown", handleDrawSelection);

            return () => {
                map.off("mousedown", handleDrawSelection);
            };
        }

        return undefined;
    }, [mapRef, mode.name, mode.props?.type, handleDrawSelection]);
};
